import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { 
  DnvMapState, LayerState, DnvMarker, CenterMapZoomed, DnvMapAction, PlainMapFunctionsOnly, ResetSelected, ResizeMap, SetMapClusterView, 
  SetMapFeatureLayers, SetMapShowFindMe, SetShowDrawControl, SetZoom, SyncMarkersActive, ToggleMarkerActive 
} from 'dnv-lib';
import saveAs from 'file-saver';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Problem } from 'src/app/models/problem';
import { 
  ApplicationState, DashboardView, MenuDictionaries, MenuItem, getApplicationState, getAppView, getDashboardView, GetDashboardView, 
  getDictionaries, getLayerState, getMapState, getMarkers, GetMenuOption, getMenus, getNoPin, getNoProblems, getProblemsFiltered, 
  GetRecentProblemsList, getSelectedProblem, getViewLoading, GoToProblemDetails, SaveDashboardView, SelectProblem, SetAppView, 
  SetDashboardView, SetNoMapLocation, SetNoProblems, SetProblemMarkers, SetProblemsFiltered, SetViewLoading, getRecentProblems 
} from 'src/app/state-management';

@Component({
    selector: 'app-recent-problems',
    templateUrl: './recent-problems.component.html',
    styleUrls: ['./recent-problems.component.css'],
    standalone: false
})
export class RecentProblemsComponent implements OnInit {
  @Input() viewLoaded: string;

  application$: Observable<ApplicationState>;
  mapState$: Observable<DnvMapState>;
  layerState$: Observable<LayerState>;
  dashboardView$: Observable<DashboardView>;
  problems$: Observable<Problem[]>;
  problemsFiltered$: Observable<Problem[]>;
  noPin$: Observable<boolean>;
  markers$: Observable<DnvMarker[]>;
  selectedProblem$: Observable<string>;
  noProblems$: Observable<boolean>;
  menuDictionaries$: Observable<MenuDictionaries>;
  menuItem$: Observable<MenuItem>;
  appView$: Observable<string>;
  viewLoading$: Observable<boolean>;
  lcm$: Observable<any[]>;

  markers: DnvMarker[];
  selectedProblem = '';
  selectedMarker = '';
  oldMarker = '';
  problems: Problem[] = [];
  problemsFiltered: Problem[] = [];
  showSpinner = true;
  resetZoom = true;  // only resets onload
  hybridList = true;
  dashboardView: string;
  toggleProblem: Problem;
  menuDictionaries: MenuDictionaries;
  menuItems: MenuItem;
  intervalTimer: number;
  problemListPopulator: Function;

  shouldRefreshListViewGrid = false;
  isRecentsView = true;
  
  clearFilterSubject: Subject<void> = new Subject();
  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    private store: Store<{}>,
    private deviceService: DeviceDetectorService,
    private router: Router
  ) {
    this.application$ = this.store.select(getApplicationState);
    this.mapState$ = this.store.select(getMapState);
    this.menuDictionaries$ = this.store.select(getDictionaries);
    this.menuItem$ = this.store.select(getMenus);
    this.dashboardView$ = this.store.select(getDashboardView);
    this.layerState$ = this.store.select(getLayerState);
    this.problems$ = this.store.select(getRecentProblems);
    this.problemsFiltered$ = this.store.select(getProblemsFiltered);
    this.noPin$ = this.store.select(getNoPin);
    this.markers$ = this.store.select(getMarkers);
    this.selectedProblem$ = this.store.select(getSelectedProblem);
    this.noProblems$ = this.store.select(getNoProblems);
    this.appView$ = this.store.select(getAppView);
    this.viewLoading$ = this.store.select(getViewLoading);
  }

  ngOnDestroy() {
    clearInterval(this.intervalTimer);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit() {
    this.store.dispatch(new GetMenuOption());
    this.store.dispatch(new GetDashboardView());
    this.store.dispatch(new SetNoProblems(true));
    this.store.dispatch(new SetShowDrawControl(false));
    this.store.dispatch(new PlainMapFunctionsOnly(true));
    this.store.dispatch(new SetNoMapLocation(false));
    this.store.dispatch(new SetMapFeatureLayers([]));
    this.store.dispatch(new SelectProblem(''));
    this.store.dispatch(new ResetSelected());
    this.store.dispatch(new SetZoom(1));
    this.store.dispatch(new SetMapClusterView(false));
    this.store.dispatch(new SetMapShowFindMe(false));

    this.problemListPopulator = () => {
      this.store.dispatch(new GetRecentProblemsList());
      this.store.dispatch(new SyncMarkersActive(this.selectedProblem));
      this.shouldRefreshListViewGrid = true;
    };
    
    this.markers$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(m => { this.markers = m; });
    this.problems$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(p => { this.problems = p; });
    this.menuDictionaries$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(m => { this.menuDictionaries = m; });
    this.menuItem$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(m => this.menuItems = m);
    this.selectedProblem$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(sp => { this.selectedProblem = sp; });

    this.problemListPopulator();
    this.intervalTimer = setInterval(this.problemListPopulator, 300000);

    // detect device type
    if (this.deviceService.isDesktop()) {
      this.store.dispatch(new SetAppView('desktop'));
    } else if (this.deviceService.isTablet()) {
      this.store.dispatch(new SetAppView('tablet'));
    } else if (this.deviceService.isMobile()) {
      this.store.dispatch(new SetAppView('mobile'));
    }
  }

  onViewSelected(view: string) {
    this.store.dispatch(new SetViewLoading(true));
    setTimeout(() => {
      this.store.dispatch(new SetDashboardView(view));
      this.store.dispatch(new ResizeMap(true));
      this.store.dispatch(new SetNoMapLocation(false));
    }, 100);
  }

  onProblemsFilter(problemsFiltered: Problem[]) {
    this.store.dispatch(new SetProblemsFiltered(problemsFiltered));
    this.store.dispatch(new SetProblemMarkers({ problem: problemsFiltered, showToolTip: true }));
  }

  onClearFilters() {
    this.clearFilterSubject.next();
  }

  routeProblemDetail(problemNo: string) {
    this.store.dispatch(new SetNoProblems(true));
    this.router.navigateByUrl('/problemdetails?ProblemNo=' + problemNo);
  }

  onMapUpdate(action: DnvMapAction) {
    this.store.dispatch(action);
  }

  onToggleProblem(problem: Problem) {
    if (this.toggleProblem === problem) {
      this.toggleProblem = null;
    } else {
      this.toggleProblem = this.problems.filter(p => p.problemNo === problem.problemNo)[0];
    }

    this.store.dispatch(new ToggleMarkerActive(problem.problemNo));
    // if a different problem was previously selected, toggle it too
    if (this.selectedProblem.length > 0 && this.selectedProblem !== problem.problemNo) {
      this.store.dispatch(new ToggleMarkerActive(this.selectedProblem));
    }

    const problemMarker: DnvMarker = this.markers.find(m => m.id === problem.problemNo);

    // toggle 'No Map Location' notification
    if (this.selectedProblem !== problem.problemNo && problemMarker
      && (problemMarker.centerPoint.lat === 0 || problemMarker.centerPoint.lng === 0)) {
      this.store.dispatch(new SetNoMapLocation(true));
    } else {
      this.store.dispatch(new SetNoMapLocation(false));
    }

    // center map to problem latlng
    if (problemMarker && problemMarker.centerPoint.lat !== 0 && problemMarker.centerPoint.lng !== 0) {
      this.store.dispatch(new CenterMapZoomed({ centerPoint: problemMarker.centerPoint, zoomLevel: 7 }));
    }

    // this.store.dispatch(new ToggleProblemExpanded(problem.problemNo));
    if (this.selectedProblem.length === 0) {
      // open problem, nothing selected before
      this.store.dispatch(new SelectProblem(problem.problemNo));

    } else if (problem.problemNo === this.selectedProblem) {
      // close prev problem, same problem selected
      this.store.dispatch(new SelectProblem(''));

    } else {
      // open new problem, something selected before
      // close prev prob first
      //  this.store.dispatch(new ToggleProblemExpanded(this.selectedProblem));
      this.store.dispatch(new SelectProblem(problem.problemNo));
    }
  }

  onToggleHybridList() {
    this.hybridList = !this.hybridList;
  }

  onProblemNumEmit(problemNo: string) {
    this.store.dispatch(new GoToProblemDetails({ problemNo: problemNo, activityNo: '' }));
  }

  onDownload() {
    const excelProbs = Object.assign([], this.problems);
    const headerKey = ['problemNo', 'priority', 'lastViewedAt', 'problemType_RefId', 'problemLocation', 'problemStatus', 'assignedTo', 'parent']
    const header = ['Case Number', 'priority', 'Viewed', 'Type', 'Location', 'Status', 'Assigned To', 'Parent'];

    const replacer = (key: string, value: any) => (value === null ? '' : value);
    const csv = excelProbs.map((row) =>
      headerKey
        .map((fieldName: string) =>
          JSON.stringify(this.getValue(fieldName, row), replacer))
        .join(',')
    );

    csv.unshift(header.join(','));
    const csvArray = csv.join('\r\n');

    const blob = new Blob([csvArray], { type: 'text/csv' });
    const pipe = new DatePipe('en-CA');
    const dateStr = pipe.transform(new Date(), 'yyyyMMddhhmmss');
    // console.log(csvArray);
    saveAs(blob, 'dashboard-export-' + dateStr + '.csv');
  }

  onSaveDefaultView(setting: string) {
    this.store.dispatch(new SaveDashboardView(setting));
  }

  getValue(key: any, obj: Problem) {
    const pipe = new DatePipe('en-CA');
    if (key === 'problemType_RefId') {
      const probType = this.menuDictionaries.problemTypes.get(obj[key]);
      if (!probType && obj.type !== '') { // child problem
        return obj.type;
      }
      return probType !== null ? probType.problemType_Name : '';
    } else if (key === 'reportedDate') {
      return pipe.transform(obj[key], 'yyyy-MM-dd');
    } else if (key === 'assignedTo') {
      return obj[key] === '' ? 'Unassigned' : obj[key];
    } else {
      return obj[key];
    }
  }

  onViewLoaded(viewLoaded: boolean) {
    this.store.dispatch(new SetViewLoading(false));
  }

  updateListViewGridState(listViewRefreshState: boolean) {
    this.shouldRefreshListViewGrid = listViewRefreshState;
  }

  convertCaseNoToURL(problemNo: string): string {
    const webRoot = `https://${window.location.host}`;
    const problemURL = `${webRoot}/problemdetails?ProblemNo=${problemNo}`;
    return problemURL;
  }

  findLastIndex(array: any[], searchKey: string | number, searchValue: any) {
    const index = array.slice().reverse().findIndex(x => x[searchKey] === searchValue);
    const count = array.length - 1;
    const finalIndex = index >= 0 ? count - index : index;
    // console.log(finalIndex);
    return finalIndex;
  }
}
